// Phase 0 / B0 deliverable 5 — route guard (ADR-007 + ADR-014 §5).
//
// Wraps protected routes: while OIDC resolves it shows a loading state;
// when no session exists it kicks off the authorization-code + PKCE
// redirect against the `customers` realm; once authenticated it renders the
// route. This guard is a CONVENIENCE — every route is independently gated
// server-side (ADR-017, ADR-014 §1). Hiding the UI is not a security
// control; the frontend is never a trust boundary (ADR-008).
//
// Usage in router.tsx — wrap the shell element:
//   { path: "/", element: <RequireAuth><AppShell .../></RequireAuth>, ... }

import { useEffect, useRef, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useLocation } from "react-router";

import { getSigninState } from "./return-to";
import { Button } from "@novemai/components";

import { useSessionTermination, useSessionTerminationPhase } from "./session-termination";

export interface RequireAuthProps {
  readonly children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps): ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const location = useLocation();
  const coordinator = useSessionTermination();
  const termination = useSessionTerminationPhase();
  const terminationRedirected = useRef(false);

  // Kick off the redirect exactly once we know there's no session and OIDC
  // isn't already mid-flight. `activeNavigator` is set during the
  // signin/silent-renew dance — don't double-trigger it.
  useEffect(() => {
    if (termination !== "active") {
      if (termination === "ended" && !terminationRedirected.current) {
        terminationRedirected.current = true;
        void auth.signinRedirect({ state: getSigninState(location) });
      }
      return;
    }
    if (
      !auth.isLoading &&
      !auth.isAuthenticated &&
      auth.activeNavigator === undefined &&
      !auth.error
    ) {
      void auth.signinRedirect({ state: getSigninState(location) });
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error, auth, location, termination]);

  if (termination === "signout_failed") {
    return (
      <div className="mx-auto max-w-xl space-y-4 p-6 text-sm text-[var(--fg-primary)]">
        <p role="alert">{t("auth.signout_failed")}</p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" disabled={auth.activeNavigator !== undefined} onClick={() => { void auth.signinRedirect({ state: getSigninState(location), prompt: "login" }); }}>{t("auth.sign_in_again")}</Button>
          <Button disabled={auth.activeNavigator !== undefined} onClick={coordinator.retrySignOut}>{t("auth.signout_retry")}</Button>
        </div>
      </div>
    );
  }

  // A background renew error must not remove authenticated queries, even
  // after the old token expires: their next 401 drives explicit recovery.
  const recoverableBackgroundError = auth.error?.source === "renewSilent" && auth.isAuthenticated;
  if (auth.error && !recoverableBackgroundError && (termination === "active" || auth.error.source === "signinRedirect")) {
    return (
      <div className="p-6 text-sm text-[var(--color-foreground)]" role="alert">
        {t("auth.callback_error")}
      </div>
    );
  }

  if (auth.isAuthenticated && termination === "active") {
    return <>{children}</>;
  }

  // Loading / redirect-pending state.
  return (
    <div
      className="p-6 text-sm text-[var(--color-muted-foreground)]"
      aria-live="polite"
    >
      {t("auth.loading")}
    </div>
  );
}
